import React, { useState } from 'react';
import axios from 'axios';
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import authConfig from '../../authConfig';
import { Form, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';

const PdfUploader = ({ trainingId, onUpload }) => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);

    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const account = useAccount(accounts[0] || {});

    const handleFileChange = (e) => {
        setError(null);
        if (e.target.files.length > 0) {
            const selected = e.target.files[0];
            const nameOk = selected.name && selected.name.toLowerCase().endsWith('.pdf');
            const typeOk = selected.type === 'application/pdf';
            if (!nameOk && !typeOk) {
                setFile(null);
                setError('Only PDF files are allowed. Please select a .pdf file.');
                e.target.value = null;
                return;
            }
            setFile(selected);
        } else {
            setFile(null);
        }
    };

    const acquireToken = async () => {
        try {
            const response = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            return response?.accessToken ?? null;
        } catch (silentError) {
            try {
                await instance.acquireTokenRedirect({
                    scopes: authConfig.api.scopes,
                    account: account
                });
            } catch (redirectErr) {
                console.error('Token redirect failed', redirectErr);
            }
            return null;
        }
    };

    const uploadPdf = async () => {
        setError(null);

        if (!isAuthenticated) {
            try {
                await instance.loginRedirect();
            } catch (loginErr) {
                console.error('Login redirect failed', loginErr);
                setError('Login required to upload files.');
            }
            return;
        }

        if (!file) {
            setError('No file selected.');
            return;
        }

        setIsUploading(true);

        try {
            const token = await acquireToken();
            if (!token) {
                setError('Unable to acquire access token.');
                setIsUploading(false);
                return;
            }

            const axiosInstance = axios.create({
                headers: { Authorization: `Bearer ${token}` }
            });

            const formData = new FormData();
            formData.append('file', file);
            formData.append('trainingId', trainingId);

            const resp = await axiosInstance.post(authConfig.api.urls.getMemberTrainingUploadPdf, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const data = resp?.data;
            if (data) {
                if (typeof onUpload === 'function') {
                    try { onUpload(file.name, data.url); } catch (cbErr) { console.error('onUpload callback error', cbErr); }
                }
            }
        } catch (err) {
            console.error('PDF upload failed:', err);
            setError(err?.response?.data || err?.message || 'Upload failed');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Form>
            <Form.Group controlId="pdfFile">
                <Form.Label>Select PDF file</Form.Label>
                <Form.Control
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileChange}
                    disabled={isUploading}
                />
                {file && (
                    <Form.Text className="text-muted">
                        Selected: {file.name} ({Math.round(file.size / 1024)} KB)
                    </Form.Text>
                )}
            </Form.Group>

            <Row className="mt-3">
                <Col xs="auto">
                    <Button variant="primary" onClick={uploadPdf} disabled={!file || isUploading}>
                        {isUploading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{' '}
                                Uploading...
                            </>
                        ) : (
                            'Upload'
                        )}
                    </Button>
                </Col>
            </Row>

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
        </Form>
    );
};

export default PdfUploader;
